import * as ExcelJS from 'exceljs';
import {
  ElementTypeEnum,
  FormElement,
  IssueTypeWithFormData,
} from 'utils/interfaces/form.util';

/** Labels per workflow type id (sorted by position), from WorkflowStateStore after load. */
export type WorkflowImportLabelsByTypeId = ReadonlyMap<string, readonly string[]>;

/** 1-based column index → Excel column letters (1→A, 26→Z, 27→AA). */
export function columnIndexToLetters(columnIndex: number): string {
  let result = '';
  let n = columnIndex;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

export function uniqueSortedEmails(emails: readonly string[]): string[] {
  return [
    ...new Set(
      emails.map((e) => e.trim()).filter((e) => e.length > 0)
    ),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export async function generateExcel(
  issueType: IssueTypeWithFormData,
  workflowLabelsByWorkflowTypeId?: WorkflowImportLabelsByTypeId,
  /** Emails for assignee / people-picker when `users` on the issue type is empty (org directory). */
  organizationUserEmails?: readonly string[]
) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(issueType.name);

  issueType.formData.elements.forEach((element) => {
    const valLength = worksheet.getRow(1).actualCellCount + 1;

    worksheet.getRow(1).getCell(valLength).value = element.name;

    const dropdownValues: string[] = generatePossibleAnswers(
      element,
      workflowLabelsByWorkflowTypeId,
      organizationUserEmails
    );
    if (dropdownValues.length > 0) {
      worksheet.getCell(`${columnIndexToLetters(valLength)}2`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${dropdownValues.join(',')}"`],
        showErrorMessage: true,
        error: 'Please select a value from the dropdown list.',
        errorTitle: 'Invalid Input',
      };
    }

    worksheet.getColumn(valLength).width = 20;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return buffer;
}

export function downloadTemplate(
  inputFile: BlobPart,
  fileName = 'Template Document.xlsx'
) {
  const newBlob = new Blob([inputFile], {
    type: EXCEL_TYPE,
  });
  const data = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = data;
  link.download = sanitizeFileName(fileName);
  link.click();
  link.remove();
  URL.revokeObjectURL(data);
}

export function sanitizeFileName(name: string): string {
  const sanitized = name.replace(/[\\/:*?"<>|]/g, '_').trim();
  if (!sanitized.endsWith('.xlsx')) {
    return `${sanitized}.xlsx`;
  }
  return sanitized;
}

function generatePossibleAnswers(
  element: FormElement,
  workflowLabelsByWorkflowTypeId?: WorkflowImportLabelsByTypeId,
  organizationUserEmails?: readonly string[]
): string[] {
  let dropdownValues: string[] = [];
  if (element.type === ElementTypeEnum.CHECKBOX) {
    dropdownValues = element.options.map(({ text }) => text);
  } else if (element.type === ElementTypeEnum.DROPDOWN) {
    dropdownValues = element.options;
  } else if (element.type === ElementTypeEnum.PEOPLE_PICKER) {
    const fromElement =
      'users' in element && Array.isArray(element.users)
        ? element.users
            .map((u) => u.email)
            .filter((e): e is string => typeof e === 'string' && e.length > 0)
        : [];
    let emails = uniqueSortedEmails(fromElement);
    if (emails.length === 0 && organizationUserEmails?.length) {
      emails = uniqueSortedEmails(organizationUserEmails);
    }
    dropdownValues = emails;
  } else if (element.type === ElementTypeEnum.WATCHER) {
    const fromElement =
      'watchers' in element && Array.isArray(element.watchers)
        ? element.watchers
            .map((w) => w.email)
            .filter((e): e is string => typeof e === 'string' && e.length > 0)
        : [];
    let emails = uniqueSortedEmails(fromElement);
    if (emails.length === 0 && organizationUserEmails?.length) {
      emails = uniqueSortedEmails(organizationUserEmails);
    }
    dropdownValues = emails;
  } else if (element.type === ElementTypeEnum.DEPENDENT_DROPDOWN) {
    element.options.forEach((option) => {
      option.children.forEach((child) => {
        dropdownValues.push(`${option.text} | ${child}`);
      });
    });
  } else if (element.type === ElementTypeEnum.WORKFLOW) {
    const fromOptions = element.options?.filter((opt) => opt.id !== null);
    if (fromOptions?.length) {
      dropdownValues = fromOptions.map((opt) => opt.label);
    } else if (
      workflowLabelsByWorkflowTypeId &&
      'workflowTypeId' in element &&
      element.workflowTypeId
    ) {
      const labels = workflowLabelsByWorkflowTypeId.get(element.workflowTypeId);
      if (labels?.length) {
        dropdownValues = [...labels];
      }
    }
  }
  return dropdownValues;
}

export const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
